/**
 * C-GTCC-VIEW-OVW1 — the read shape every GTCC endpoint client shares.
 *
 * `api/fleet.ts` (C-GTCC-COCKPIT-INFRA1) carries its own copy of this union
 * because it transcribes the G-GTCC-API-FLEET1 contract and is rebased onto
 * G's DTO module once G merges. The three clients this ticket adds
 * (`ops.ts`, `svc.ts`, `series.ts`, plus `risk.ts` on the already existing
 * risk-radar route) are additive and share one definition instead of copying
 * the union four more times.
 *
 * Read-only by construction: `apiGet` only, no mutation verb, no upstream URL
 * and no PromQL — same rule as `apps/admin/src/monitoring/api.ts:74`.
 *
 * Freshness and verdicts are never computed here. `status` and
 * `freshnessSeconds` arrive decided by the API (Owner decisions P8/P9/P10: a
 * stale source is never drawn as healthy, an unmeasurable value reads
 * "nicht messbar" and is never replaced by a number nobody measured).
 */
import { apiGet, ApiError } from "../../lib/api.js";

/** The closed status enum of the GTCC API convention. */
export const GTCC_STATUSES = ["healthy", "warning", "critical", "stale", "unmeasurable"] as const;
export type GtccStatus = (typeof GTCC_STATUSES)[number];

/** Per-row verdict — the dot colour of the reference sheet. */
export const GTCC_VERDICTS = ["ok", "warn", "crit", "info", "unmeasurable"] as const;
export type GtccVerdict = (typeof GTCC_VERDICTS)[number];

/**
 * Every response of the convention carries when it was measured and how old
 * that measurement is. `freshnessSeconds` stays optional: the per-endpoint
 * sketches spell it out for some routes only, and a missing age makes the
 * badge fall back to the measurement timestamp — never to the local clock.
 */
export interface GtccMeasured {
  readonly measuredAt: string;
  readonly freshnessSeconds?: number;
  readonly status: GtccStatus;
}

/**
 * Same discriminated union as the monitoring client
 * (`apps/admin/src/monitoring/api.ts:34`): a view never sees `ApiError`.
 * `disabled` is the honest answer while the owning API ticket is unmerged —
 * the route answers 404 and the panel says so instead of inventing numbers.
 */
export type GtccResult<T> =
  | { readonly ok: true; readonly data: T }
  | { readonly ok: false; readonly kind: "disabled"; readonly httpStatus: 404 }
  | { readonly ok: false; readonly kind: "forbidden"; readonly httpStatus: 403 }
  | {
      readonly ok: false;
      readonly kind: "upstream";
      readonly httpStatus: number;
      readonly requestId: string | undefined;
    }
  | { readonly ok: false; readonly kind: "transport_error"; readonly message: string };

export async function gtccGet<T>(path: string, signal?: AbortSignal): Promise<GtccResult<T>> {
  try {
    return { ok: true, data: await apiGet<T>(path, undefined, signal) };
  } catch (err) {
    if (err instanceof ApiError) {
      const { status, requestId } = err;
      if (status === 404) return { ok: false, kind: "disabled", httpStatus: 404 };
      if (status === 403) return { ok: false, kind: "forbidden", httpStatus: 403 };
      return { ok: false, kind: "upstream", httpStatus: status, requestId };
    }
    const message = err instanceof Error ? err.message : "gtcc read failed";
    return { ok: false, kind: "transport_error", message };
  }
}

/**
 * The status values that make a source unusable for a number. Data Confidence
 * counts a source as fresh exactly when it is neither of these — the
 * definition the C-GTCC-VIEW-OVW1 body gives, not one of our own.
 */
export function isFreshStatus(status: GtccStatus): boolean {
  return status !== "stale" && status !== "unmeasurable";
}
